import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Message } from '@arco-design/web-react';
import get from 'lodash/get';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';

import { VolumeEmptyActionPanel } from '../../components/VolumeEmptyActionPanel';
import { useDiskPricingSync } from '../../components/diskPricingSync';
import { useStorageClassFormValidation } from '../../components/storageClassField';
import { VendorDevicePersistFields } from '../../components/vendorDevice';
import { VolumeResourceCard, buildDiskVolumeExtraItems } from '../../components/volumeResourceCard';
import type { CapabilityControlProps } from '../../core/types';
import { isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isActionVisible, isFieldVisible, resolveActionMode } from '../../platform/capability-registry/resolveFieldMode';
import { shouldSkipCapabilityFetch } from '../../platform/capability-registry/shouldSkipCapabilityFetch';
import {
  clearStoredContainerDeployConfig,
  readStoredContainerDeployConfig,
  writeStoredContainerDeployConfig,
} from '../../platform/container-deploy-config';
import { buildOptionFromRaw, mergeOptionMetaForValues, resolveOptionDisplayValue, syncOptionMetaFromRaw } from '../../platform/option-meta';
import { ImageVmCreateModal, getImageVmCreateModalValues } from './ImageVmCreateModal';
import { ImageVmSelectModal, getImageVmSelectModalValues } from './ImageVmSelectModal';
import { DISK_SIZE_MIN_GI } from './constants';
import { IMAGE_VM_ACTION_KEYS, IMAGE_VM_FIELD_KEYS, IMAGE_VM_OPTION_META_KEYS } from './fieldKeys';
import styles from './index.module.less';
import type { ImageVmBlockProps, SystemDiskRecord } from './types';
import {
  buildSystemDiskFromCreateForm,
  clearSystemDiskFormField,
  diskToCardItem,
  isNewSystemDiskMissingStorageClass,
  mergeEditedSystemDisk,
  normalizeSystemDiskRecord,
} from './utils';

const FormItem = Form.Item;

type ModalMode = 'create' | 'edit' | 'select' | null;

function isActionEnabled(actionKey: string, control: CapabilityControlProps): boolean {
  return resolveActionMode(actionKey, control) === 'enabled';
}

function readSystemDisk(form: ImageVmBlockProps['form'], path: string): SystemDiskRecord | undefined {
  return normalizeSystemDiskRecord(form.getFieldValue(path));
}

function writeSystemDisk(form: ImageVmBlockProps['form'], path: string, disk: SystemDiskRecord | undefined) {
  form.setFieldValue(path, disk);
}

/**
 * 云主机 / EBM 系统盘能力块（registry: image-vm）。
 * L1 只写 `systemDisk` 本盘字段 + 持久化 containerDeployConfig / optionMeta；
 * 服务预置由 L2 `image.applyServices` 从 deployConfig 解析（对齐 CI：镜像块不写 services）。
 */
export function ImageVmBlock(props: ImageVmBlockProps) {
  const { form, paths, options, linkageProps, ...control } = props;
  const { t } = useTranslation();
  const [createForm] = Form.useForm();
  const [selectForm] = Form.useForm();
  const [modalMode, setModalMode] = useState<ModalMode>(null);

  const isEdit = options?.isEdit === true;
  /** 自定义规格下单：允许改容量（镜像在弹窗内只读） */
  const allowOrderCustomQuantityEdit = options?.resourceEditMode === 'order-custom';
  /** 下单：SKU 预置已有系统盘时隐藏卡片删/改 */
  const lockActionsWhenFilled = options?.lockActionsWhenFilled === true;
  const presetHasDisk = Boolean(options?.presetConfigSnapshot && normalizeSystemDiskRecord(get(options.presetConfigSnapshot, paths.disk)));
  const skipListFetch = shouldSkipCapabilityFetch(control, {
    skipFetch: options?.skipFetch,
    fieldKeys: [IMAGE_VM_FIELD_KEYS.disk],
  });
  const readonly = isFieldReadonly(IMAGE_VM_FIELD_KEYS.disk, control) || skipListFetch;
  const pendingImageItemRef = useRef<CommonImageItemType>();

  const diskRaw = Form.useWatch(paths.disk, form);
  const optionMeta = Form.useWatch('optionMeta', form);
  const disk = useMemo(() => normalizeSystemDiskRecord(diskRaw), [diskRaw]);

  const namespace = linkageProps?.namespace;
  const imageQuery = linkageProps?.imageQuery ?? {};

  const storageClassLabelMap = useMemo(() => {
    if (!disk?.storageClass) return {};
    const label = resolveOptionDisplayValue(
      { ...(form.getFieldsValue() as Record<string, unknown>), optionMeta },
      `${paths.disk}.storageClass`,
      disk.storageClass,
    );
    return label ? { [disk.storageClass]: label } : {};
  }, [disk?.storageClass, form, optionMeta, paths.disk]);

  const { pricingActive, syncItemPricing } = useDiskPricingSync(options);

  const collectSystemDiskStorageClassMissing = useCallback((value: unknown) => (isNewSystemDiskMissingStorageClass(value) ? [0] : []), []);

  const { rules: storageClassRules, singleDiskError } = useStorageClassFormValidation(
    diskRaw,
    pricingActive,
    collectSystemDiskStorageClassMissing,
    t('common:please.select.storage.class'),
  );

  const unconfiguredLabel = t('data-center:maas.storage.not.configured');

  const closeModal = useCallback(() => {
    setModalMode(null);
    pendingImageItemRef.current = undefined;
    createForm.resetFields();
    selectForm.resetFields();
  }, [createForm, selectForm]);

  const handleImageItemChange = useCallback((item: CommonImageItemType | undefined) => {
    pendingImageItemRef.current = item;
  }, []);

  const openCreate = useCallback(() => {
    createForm.resetFields();
    createForm.setFieldsValue({ diskSize: DISK_SIZE_MIN_GI });
    setModalMode('create');
  }, [createForm]);

  const openEdit = useCallback(() => {
    createForm.resetFields();
    const disk = readSystemDisk(form, paths.disk);
    createForm.setFieldsValue({ diskSize: Number(disk?.diskSize) || DISK_SIZE_MIN_GI });
    setModalMode('edit');
  }, [createForm, form, paths.disk]);

  const openSelect = useCallback(() => {
    setModalMode('select');
    selectForm.resetFields();
  }, [selectForm]);

  const handleRemove = useCallback(() => {
    clearSystemDiskFormField(form, paths.disk);
  }, [form, paths.disk]);

  const handleCreateOk = useCallback(async () => {
    const values = getImageVmCreateModalValues(createForm, pendingImageItemRef.current, {
      resourceEditMode: options?.resourceEditMode,
    });
    if (!values.image?.url) {
      Message.error(t('common:image.select'));
      return Promise.reject();
    }
    /** 仅写盘字段；apis → services 由 L2 applyImagePresetServicesEffect 负责 */
    const nextDisk: SystemDiskRecord = buildSystemDiskFromCreateForm(values);
    const merged = modalMode === 'edit' ? mergeEditedSystemDisk(disk, nextDisk) : nextDisk;
    writeSystemDisk(form, paths.disk, merged);
    syncItemPricing(form, `${paths.disk}.pricing`, merged.pricing);
    const modalValues = createForm.getFieldsValue() as Record<string, unknown>;
    const storedDeployConfig = readStoredContainerDeployConfig(modalValues);
    if (storedDeployConfig) {
      writeStoredContainerDeployConfig(form, storedDeployConfig);
    } else {
      clearStoredContainerDeployConfig(form);
    }
    mergeOptionMetaForValues(form, modalValues, [merged.storageClass, merged.reclaimPolicy]);
    const imageItem = values.imageItem;
    if (imageItem) {
      const imageKey = String(imageItem.id ?? imageItem.key ?? '').trim();
      const built = buildOptionFromRaw(imageKey, imageItem as unknown as Record<string, unknown>);
      if (built) {
        syncOptionMetaFromRaw(form, imageKey, built, IMAGE_VM_OPTION_META_KEYS.image);
      }
    }
    closeModal();
  }, [closeModal, createForm, disk, form, modalMode, options?.resourceEditMode, paths.disk, syncItemPricing, t]);

  const handleSelectOk = useCallback(async () => {
    const selectedDisk = getImageVmSelectModalValues(selectForm);
    if (!selectedDisk) {
      return Promise.reject();
    }
    const merged = isEdit ? { ...selectedDisk, locked: disk?.locked ?? true } : selectedDisk;
    writeSystemDisk(form, paths.disk, merged);
    options?.onResourceWatchSnapshot?.(form);
    closeModal();
  }, [closeModal, disk?.locked, form, isEdit, options, paths.disk, selectForm]);

  const canAdd = isActionVisible(IMAGE_VM_ACTION_KEYS.add, control) && !readonly && !isEdit;
  const canSelect = isActionVisible(IMAGE_VM_ACTION_KEYS.select, control) && !readonly;
  /** 直建 / SKU / 实例编辑均可改已填系统盘；下单锁删改由 lockActionsWhenFilled 控制 */
  const canEditAction = isActionVisible(IMAGE_VM_ACTION_KEYS.edit, control) && !readonly;
  const canRemoveAction = isActionVisible(IMAGE_VM_ACTION_KEYS.remove, control) && !readonly;

  const diskRules = useMemo(
    () =>
      readonly
        ? undefined
        : [
            ...storageClassRules,
            {
              validator(value: unknown, callback: (error?: string) => void) {
                if (!normalizeSystemDiskRecord(value)) {
                  callback(t('data-center:please.add.at.least.one.system.disk'));
                  return;
                }
                callback();
              },
            },
          ],
    [readonly, storageClassRules, t],
  );

  const hasDisk = Boolean(disk);
  const showAddPanel = !hasDisk && !readonly;

  if (!isFieldVisible(IMAGE_VM_FIELD_KEYS.disk, control)) {
    return null;
  }

  const cardItem = disk ? diskToCardItem(disk, storageClassLabelMap) : undefined;
  const actionsLockedByFill = lockActionsWhenFilled && presetHasDisk;
  /** 自定义下单：有盘时仍禁止删，但保留编辑以补填/修改容量 */
  const canEditOnCard = Boolean(
    cardItem &&
      canEditAction &&
      isActionEnabled(IMAGE_VM_ACTION_KEYS.edit, control) &&
      (!actionsLockedByFill || allowOrderCustomQuantityEdit),
  );
  const canDeleteOnCard = Boolean(
    cardItem &&
      canRemoveAction &&
      isActionEnabled(IMAGE_VM_ACTION_KEYS.remove, control) &&
      (!isEdit || !cardItem.locked) &&
      !actionsLockedByFill,
  );

  return (
    <>
      <FormItem label={t('cluster:system-image')} required={!readonly} style={{ margin: 0 }}>
        <FormItem field={paths.disk} rules={diskRules} noStyle>
          <Input style={{ display: 'none' }} />
        </FormItem>
        {pricingActive ? <VendorDevicePersistFields fieldPrefix={`${paths.disk}.pricing`} /> : null}

        <div className={styles.block}>
          {cardItem ? (
            <div className={styles.list}>
              <VolumeResourceCard
                value={cardItem.diskName}
                label={cardItem.diskNickname || cardItem.diskName}
                extraItems={buildDiskVolumeExtraItems(cardItem, t, {
                  emptyStorageClassFallback: !pricingActive ? unconfiguredLabel : undefined,
                })}
                error={singleDiskError}
                canEdit={canEditOnCard}
                canDelete={canDeleteOnCard}
                onEdit={openEdit}
                onDelete={handleRemove}
              />
            </div>
          ) : null}

          {showAddPanel ? (
            <VolumeEmptyActionPanel
              items={[
                ...(canAdd && isActionEnabled(IMAGE_VM_ACTION_KEYS.add, control)
                  ? [{ key: 'create', title: t('components:create.system.disk'), onClick: openCreate }]
                  : []),
                ...(canSelect && isActionEnabled(IMAGE_VM_ACTION_KEYS.select, control)
                  ? [{ key: 'select', title: t('components:add.existing.system.disk'), onClick: openSelect }]
                  : []),
              ]}
            />
          ) : null}
        </div>
      </FormItem>

      <ImageVmCreateModal
        visible={modalMode === 'create' || modalMode === 'edit'}
        form={createForm}
        initDisk={modalMode === 'edit' ? disk : undefined}
        imageQuery={imageQuery}
        billModelId={options?.billModelId}
        deploymentTypeId={options?.deploymentTypeId}
        paymentTiming={options?.paymentTiming}
        skuType={options?.skuType}
        resourceEditMode={options?.resourceEditMode}
        resourceCode={options?.resourceCode}
        onImageItemChange={handleImageItemChange}
        onOk={handleCreateOk}
        onCancel={closeModal}
      />

      <ImageVmSelectModal
        visible={modalMode === 'select'}
        form={selectForm}
        namespace={namespace}
        onOk={handleSelectOk}
        onCancel={closeModal}
      />
    </>
  );
}
